import styled from 'styled-components';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';

export const SidebarContainer = styled(motion.aside).withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  width: 280px;
  flex-shrink: 0;
  background-color: var(--background-primary);
  border-right: 1px solid var(--border-color);
  height: calc(100vh - 64px);
  overflow-y: auto;
  position: sticky;
  top: 64px;
  display: ${props => props.isOpen ? 'block' : 'none'};

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 56px;
    left: 0;
    z-index: 1000;
    /* Slide with transform (compositor-only) rather than animating left, which
       re-lays-out every frame. Visibility flips after the slide-out finishes, so a
       closed panel can't be tabbed into and its shadow doesn't peek in at the edge. */
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: transform 0.3s ease, visibility 0s linear ${props => props.isOpen ? '0s' : '0.3s'};
    will-change: transform;
    box-shadow: var(--shadow-lg);
    height: calc(100vh - 56px);
    height: calc(100dvh - 56px); /* tracks the mobile browser toolbar instead of jumping as it shows/hides */
  }
`;

export const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--background-secondary);
  text-align: center;
  
  h3 {
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    font-size: 1.5rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    letter-spacing: -0.025em;
  }
  
  p {
    color: var(--text-muted);
    font-size: 0.875rem;
    font-family: 'Inter', sans-serif;
  }
`;

export const SearchSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--background-primary);
`;

export const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  font-size: 0.875rem;
  background-color: var(--background-secondary);
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: var(--background-primary);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
`;

export const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.$show ? 'block' : 'none'};
`;

export const SearchResultItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

export const SearchResultTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

export const SearchResultSubtitle = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

export const NoResults = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.875rem;
`;


export const NavSection = styled.div`
  padding: 1rem 0;

  /* Sections default to collapsed (see useSidebarCollapsedSections.js), so on
     a narrow screen the sidebar is mostly a stack of bare headings — full
     desktop padding between each one reads as too much dead space there. */
  @media (max-width: 768px) {
    padding: 0.4rem 0;
  }
`;

export const SectionTitle = styled.h4`
  color: var(--accent-color);
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.15px;
  padding: 0 1.5rem;
  margin-bottom: 0.5rem;
`;

// Same look as SectionTitle, but a full-width button so the whole section
// name is clickable to collapse/expand it. Kept as a distinct component
// (rather than making SectionTitle itself a button) so non-collapsible
// callers are unaffected. Icon sits immediately before the label (modern
// disclosure-tree layout) rather than pushed to the far right.
export const SectionToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.15px;
  padding: 0 1.5rem;
  /* Only need breathing room before the item list when it's actually there —
     a collapsed section has no NavList below it, so this would just be dead
     trailing space stacking up across every collapsed heading. */
  margin-bottom: ${(props) => (props.$collapsed ? 0 : '0.5rem')};

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

export const SectionToggleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
`;

export const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const NavItem = styled.li`
  margin: 0;
`;

export const NavLinkStyled = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  /* Left padding lines the item's icon up with where the section heading's
     label text starts (SectionToggle's 1.5rem padding + its 14px icon box +
     8px gap = 2.875rem), not with the heading's own +/- icon. */
  padding: 0.75rem 1.5rem 0.75rem 2.875rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: var(--transition);
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-secondary);
    text-decoration: none;
  }

  &.active {
    background-color: rgba(216, 184, 137, 0.1);
    color: var(--text-primary);
    border-right: 3px solid var(--primary-color);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }

  /* Hover: the icon fills solid (.nav-icon-base), and the same icon is drawn again
     on top as white lines only (.nav-icon-lines) so any detail inside the shape
     (a clock's hands, a door, a check mark) shows instead of vanishing. */
  .nav-icon-base {
    fill: transparent;
    transition: color 0.2s ease, fill 0.2s ease, stroke-width 0.2s ease;
  }

  /* Thin white line-work over the bolder dark layer: the icon reads as the inverse of
     its normal self (white lines on a dark shape), and icons made only of lines
     (a bar chart, a check) stay visible as dark strokes with a white center. */
  .nav-icon-lines {
    fill: none;
    stroke: #FFFFFF;
    stroke-width: 1.25;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  /* The selected page keeps the filled (dark) icon, same as hover. */
  &:hover .nav-icon-base,
  &:focus-visible .nav-icon-base,
  &.active .nav-icon-base {
    color: var(--brand-brown-deep);
    fill: var(--brand-brown-deep);
    stroke-width: 2.8;
  }

  &:hover .nav-icon-lines,
  &:focus-visible .nav-icon-lines,
  &.active .nav-icon-lines {
    opacity: 1;
  }

  body.dark-mode &:hover .nav-icon-base,
  body.dark-mode &:focus-visible .nav-icon-base,
  body.dark-mode &.active .nav-icon-base {
    color: var(--brand-gold);
    fill: var(--brand-gold);
  }

  body.dark-mode .nav-icon-lines {
    stroke: var(--background-primary);
  }
`;

// Holds the two stacked copies of a nav icon (see NavLinkStyled).
export const NavIconSlot = styled.span`
  position: relative;
  display: inline-block;
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  svg {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

export const MobileOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  z-index: 999;
  display: none;
  
  @media (max-width: 768px) {
    /* Fade with CSS in step with the panel's slide. It used to flip display none/block,
       which cut the overlay off instantly on close while the panel was still sliding out. */
    display: block;
    opacity: ${props => props.isOpen ? 1 : 0};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
    transition: opacity 0.3s ease, visibility 0s linear ${props => props.isOpen ? '0s' : '0.3s'};
  }
`;

export const QuickStats = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--background-secondary);
`;

export const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .label {
    color: var(--text-secondary);
    font-size: 0.875rem;
  }
  
  .value {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.875rem;
  }
`;
